import { z } from 'zod';
import { Id, IsoDate, LawCode, PageQuery, Timestamp } from './common.js';

export const Direction = z.enum(['in', 'out']);
export const Channel = z.enum([
  'WhatsApp', 'Email', 'LinkedIn DM', 'Telephone', 'Online demo', 'Personal visit', 'Meeting', 'Web form', 'Note',
]);
export type Channel = z.infer<typeof Channel>;

export const LogEntry = z.object({
  id: Id,
  relationshipId: Id,
  date: IsoDate,
  time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable(),
  dir: Direction,
  channel: Channel,
  summary: z.string(),
  /** Server-derived: Note never counts; meetings count as neither. */
  countsAsTouch: z.boolean(),
  byUserId: Id,
  createdAt: Timestamp,
});
export type LogEntry = z.infer<typeof LogEntry>;

export const CreateLogEntryRequest = z
  .object({
    date: IsoDate,
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).optional(),
    dir: Direction,
    channel: Channel,
    summary: z.string().trim().min(1).max(5000),
  })
  .strict();
export type CreateLogEntryRequest = z.infer<typeof CreateLogEntryRequest>;
export const LogListQuery = PageQuery;

export const CommitmentStatus = z.enum(['open', 'kept', 'broken', 'cancelled']);
export const Commitment = z.object({
  id: Id,
  relationshipId: Id,
  text: z.string().min(1),
  /** Who promised: the counterpart or us. */
  by: z.enum(['them', 'us']),
  dueDate: IsoDate.nullable(),
  status: CommitmentStatus,
  ownerUserId: Id,
  version: z.number().int().nonnegative(),
  createdAt: Timestamp,
});
export type Commitment = z.infer<typeof Commitment>;

export const CreateCommitmentRequest = z
  .object({ text: z.string().trim().min(1).max(2000), by: z.enum(['them', 'us']), dueDate: IsoDate.optional() })
  .strict();
export type CreateCommitmentRequest = z.infer<typeof CreateCommitmentRequest>;
export const UpdateCommitmentRequest = z
  .object({ text: z.string().trim().min(1).max(2000), dueDate: IsoDate.nullable(), status: CommitmentStatus })
  .partial()
  .extend({ version: z.number().int().nonnegative() })
  .strict();
export type UpdateCommitmentRequest = z.infer<typeof UpdateCommitmentRequest>;

export const DraftStatus = z.enum(['pending', 'sent', 'discarded']);
/** Client message drafts: never auto-sent; the owner copies and sends by hand. */
export const Draft = z.object({
  id: Id,
  relationshipId: Id,
  ownerUserId: Id,
  channel: Channel.exclude(['Note', 'Online demo', 'Personal visit', 'Meeting', 'Telephone']),
  purpose: z.string(),
  body: z.string(),
  status: DraftStatus,
  judgeVerdict: z.object({ pass: z.boolean(), laws: z.array(LawCode), notes: z.string() }).nullable(),
  version: z.number().int().nonnegative(),
  createdAt: Timestamp,
  sentAt: Timestamp.nullable(),
});
export type Draft = z.infer<typeof Draft>;

export const DraftListQuery = PageQuery.extend({
  relationshipId: Id.optional(),
  status: DraftStatus.optional(),
  owner: z.union([Id, z.literal('me')]).optional(),
});
export type DraftListQuery = z.infer<typeof DraftListQuery>;

export const CreateDraftRequest = z
  .object({
    relationshipId: Id,
    channel: Draft.shape.channel,
    purpose: z.string().trim().min(1).max(200),
    body: z.string().trim().min(1).max(10000),
  })
  .strict();
export type CreateDraftRequest = z.infer<typeof CreateDraftRequest>;
export const UpdateDraftRequest = z
  .object({ body: z.string().trim().min(1).max(10000), purpose: z.string().trim().min(1).max(200), status: z.literal('discarded') })
  .partial()
  .extend({ version: z.number().int().nonnegative() })
  .strict();
export type UpdateDraftRequest = z.infer<typeof UpdateDraftRequest>;

/** POST /drafts/:id/mark-sent — guards run server-side. */
export const MarkSentRequest = z
  .object({
    version: z.number().int().nonnegative(),
    /** The text actually sent, if edited after copying. */
    sentBody: z.string().trim().min(1).max(10000).optional(),
    /** Owner explicitly overrides a WARN-level guard (e.g. plan hold); ignored for BLOCK. */
    overrideReason: z.string().trim().min(5).max(500).optional(),
  })
  .strict();
export type MarkSentRequest = z.infer<typeof MarkSentRequest>;

export const GuardOutcome = z.enum(['allow', 'warn', 'block']);
export const GuardVerdict = z.object({
  outcome: GuardOutcome,
  law: LawCode.nullable(),
  message: z.string(),
});
export type GuardVerdict = z.infer<typeof GuardVerdict>;

export const MarkSentResponse = z.discriminatedUnion('recorded', [
  z.object({ recorded: z.literal(true), verdict: GuardVerdict, draft: Draft, logEntry: LogEntry }),
  z.object({ recorded: z.literal(false), verdict: GuardVerdict.extend({ outcome: z.enum(['warn', 'block']) }) }),
]);
export type MarkSentResponse = z.infer<typeof MarkSentResponse>;
